if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      syncProductQuantityToCart = undefined;
      stickyQuantityButtonHandler = undefined;
      stickyQuantitySyncHandler = undefined;
      stickyVisibilityHandler = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initStickyAtc();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        this.syncProductQuantityToCart = debounce(this.handleProductQuantityChange.bind(this), ON_CHANGE_DEBOUNCE_TIMER);
        this.quantityInput.addEventListener('change', this.syncProductQuantityToCart);
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.quantityInput && this.syncProductQuantityToCart) {
          this.quantityInput.removeEventListener('change', this.syncProductQuantityToCart);
        }
        if (this.stickyQuantityButtonHandler) {
          this.stickyAtc?.removeEventListener('click', this.stickyQuantityButtonHandler);
        }
        if (this.quantityInput && this.stickyQuantitySyncHandler) {
          this.quantityInput.removeEventListener('change', this.stickyQuantitySyncHandler);
          this.quantityInput.removeEventListener('input', this.stickyQuantitySyncHandler);
        }
        if (this.stickyVisibilityHandler) {
          window.removeEventListener('scroll', this.stickyVisibilityHandler);
          window.removeEventListener('resize', this.stickyVisibilityHandler);
        }
      }

      initStickyAtc() {
        if (!this.stickyAtc || !this.quantityInput) return;

        this.stickyQuantityButtonHandler = this.handleStickyQuantityButton.bind(this);
        this.stickyQuantitySyncHandler = this.syncStickyQuantity.bind(this);
        this.stickyAtc.addEventListener('click', this.stickyQuantityButtonHandler);
        this.quantityInput.addEventListener('change', this.stickyQuantitySyncHandler);
        this.quantityInput.addEventListener('input', this.stickyQuantitySyncHandler);
        this.syncStickyQuantity();
        this.initStickyAtcVisibility();
      }

      initStickyAtcVisibility() {
        this.stickyVisibilityHandler = this.updateStickyAtcVisibility.bind(this);
        window.addEventListener('scroll', this.stickyVisibilityHandler, { passive: true });
        window.addEventListener('resize', this.stickyVisibilityHandler);
        this.updateStickyAtcVisibility();
      }

      updateStickyAtcVisibility() {
        if (!this.stickyAtc) return;

        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const primarySubmitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const primaryButtonHasPassed = primarySubmitButton
          ? primarySubmitButton.getBoundingClientRect().bottom < -8
          : window.scrollY > window.innerHeight * 0.7;
        const shouldShow = isMobile && primaryButtonHasPassed;
        this.stickyAtc.classList.toggle('is-visible', shouldShow);
      }

      handleStickyQuantityButton(event) {
        const button = event.target.closest('[data-sticky-quantity-action]');
        if (!button || !this.quantityInput) return;

        const action = button.dataset.stickyQuantityAction;
        const currentValue = Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1;
        const step = Number(this.quantityInput.step) || 1;
        const min = Number(this.quantityInput.min) || 1;
        const max = this.quantityInput.max ? Number(this.quantityInput.max) : null;
        const nextValue = action === 'increase' ? currentValue + step : currentValue - step;

        this.quantityInput.value = Math.max(min, max ? Math.min(nextValue, max) : nextValue);
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncStickyQuantity();
      }

      syncStickyQuantity() {
        if (!this.stickyAtc || !this.quantityInput) return;

        const value = Number(this.quantityInput.value) || Number(this.quantityInput.min) || 1;
        const min = Number(this.quantityInput.min) || 1;
        const max = this.quantityInput.max ? Number(this.quantityInput.max) : null;
        const valueNode = this.stickyAtc.querySelector('[data-sticky-quantity-value]');
        const decreaseButton = this.stickyAtc.querySelector('[data-sticky-quantity-action="decrease"]');
        const increaseButton = this.stickyAtc.querySelector('[data-sticky-quantity-action="increase"]');

        if (valueNode) valueNode.textContent = value;
        if (decreaseButton) decreaseButton.disabled = value <= min;
        if (increaseButton) increaseButton.disabled = max !== null && value >= max;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Sticky-Price');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.syncStickySubmitButton(html);
          this.syncStickyQuantity();

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.syncStickySubmitButton();

        const selectors = ['price', 'Sticky-Price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      syncStickySubmitButton(html) {
        const stickyButton = this.querySelector(`#StickyProductSubmitButton-${this.dataset.section}`);
        if (!stickyButton) return;

        const sourceButton = html?.getElementById(`StickyProductSubmitButton-${this.sectionId}`);
        const mainButton = html?.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const disabled = sourceButton?.hasAttribute('disabled') ?? mainButton?.hasAttribute('disabled') ?? true;
        const sourceText = sourceButton?.querySelector('span')?.innerHTML || mainButton?.querySelector('span')?.innerHTML;

        stickyButton.toggleAttribute('disabled', disabled);
        if (sourceText) stickyButton.querySelector('span').innerHTML = sourceText;
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        const hasCartQuantity = data.cartQuantity > 0;
        let min = hasCartQuantity ? Math.min(data.min, data.step) : data.min;
        const max = data.max;
        if (max !== null) min = Math.min(min, max);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = hasCartQuantity ? data.cartQuantity : min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      handleProductQuantityChange(event) {
        if (this.syncingCartQuantity) return;

        const variantId = this.productForm?.variantIdInput?.value;
        const cartQuantity = parseInt(this.quantityInput.dataset.cartQuantity || '0');
        const quantity = parseInt(event.target.value);

        if (!variantId || cartQuantity <= 0 || Number.isNaN(quantity)) return;

        this.syncingCartQuantity = true;

        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const body = JSON.stringify({
          id: variantId,
          quantity,
          sections,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            this.updateCartIconBubble(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-info',
              cartData: parsedState,
              variantId,
            });
          })
          .catch((error) => console.error(error))
          .finally(() => {
            this.syncingCartQuantity = false;
          });
      }

      updateCartIconBubble(parsedState) {
        if (!parsedState.sections?.['cart-icon-bubble']) return;

        const html = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
        const currentBubble = document.getElementById('cart-icon-bubble');
        const updatedBubble = html.getElementById('cart-icon-bubble');

        if (currentBubble && updatedBubble) currentBubble.innerHTML = updatedBubble.innerHTML;
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get stickyAtc() {
        return this.querySelector('.product-mobile-sticky-atc');
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
